/**
 * ChatBot - Main chatbot component
 * Follows ADR principles:
 * - ADR-001: Complexity Management - Uses hooks for state management
 * - ADR-004: Separation of Concerns - Logic in hooks, presentation in components
 * - ADR-007: Loose Coupling - Self-contained with QueryClientProvider
 */

import React, { useEffect, useMemo, useCallback, useState, useRef } from 'react';
import { MessageCircle } from 'lucide-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ChatWindow } from './ChatWindow';
import { FullScreenChatbot } from './FullScreenChatbot';
import { setLoggingEnabled, log, logWarn, logError } from '../utils/logger';
import { useCustomerInfo } from '../hooks/useCustomerInfo';
import { useSessionWelcomeTracking } from '../hooks/useSessionWelcomeTracking';
import { useChatDisplayMode } from '../hooks/useChatDisplayMode';
import { useChatSessionState } from '../hooks/useChatSessionState';
import { useSharedChatState } from '../hooks/useSharedChatState';
import { parseJWT, isJWTExpired } from '../utils/jwt';
import { detectBrowserLanguage } from '../utils/browserLanguage';
import type { Language } from '../hooks/useTranslation';
import type { PollingConfig, AttributionType, LayoutConfig, SettingsModalRenderProps } from '../types/chat';
import type { AuthProvider } from '../utils/tokenAdapter';


/**
 * Props for the ChatBot component
 */
export interface ChatBotProps {
  /** The URL of the chat server (required) */
  serverUrl: string;
  /** The ID of the agent to chat with (required) */
  agentId: string;
  /** Agent name for display purposes (optional) */
  agentName?: string;
  /** JWT authentication token (optional) - will extract customerId and customerName if provided */
  authToken?: string;
  /** Auth provider type for token claim mapping (optional, default: 'generic') */
  authProvider?: AuthProvider;
  /** Customer ID (optional) - overrides JWT-derived value */
  customerId?: string;
  /** Customer name for personalization (optional) - overrides JWT-derived value */
  customerName?: string;
  /** Language for the interface (optional, default: detected from browser - 'en' or 'da', fallback to 'en') */
  language?: Language;
  /** Initial display mode (optional, default: 'popup') */
  initialMode?: 'popup' | 'fullscreen' | 'minimized';
  /** Custom polling configuration (optional) */
  pollingConfig?: PollingConfig;
  /** Callback when a session is created (optional) */
  onSessionCreated?: (sessionId: string) => void;
  /** Callback when chat is closed/minimized (optional) */
  onClose?: () => void;
  /** Callback when display mode changes (optional) */
  onDisplayModeChange?: (mode: 'popup' | 'fullscreen' | 'minimized') => void;
  /** Whether to enable automatic session creation on mount (default: true) */
  autoStartSession?: boolean;
  /** When true, block session creation until authToken is present (default: false) */
  requireAuth?: boolean;
  /** Attribution type: 'none' | 'enter1nine' (optional, default: 'none') */
  attribution?: AttributionType;
  /** Whether to enable console logging throughout the application (default: false) */
  enableLogging?: boolean;
  /** Record scroll/bubble/cursor layout timeline for jump debugging (default: false) */
  layoutDebug?: boolean;
  /** Force minimize the chat (external control) */
  forceMinimize?: boolean;
  /** Welcome messages per language to send when creating a new session (optional) */
  welcomeMessages?: Record<'da' | 'en', string>;
  /**
   * Layout configuration for customizing UI elements
   * Controls visibility of buttons, session history, mobile menu, etc.
   */
  layout?: LayoutConfig;
  /**
   * Render prop for settings modal in fullscreen mode
   * If provided, settings button will be shown in fullscreen mobile menu
   */
  renderSettingsModal?: (props: SettingsModalRenderProps) => React.ReactNode;
}

/**
 * Creates a QueryClient instance for the chatbot
 * This ensures the chatbot is self-contained and doesn't depend on external QueryClient
 */
const createQueryClient = (): QueryClient => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: 1,
        staleTime: 5 * 60 * 1000 // 5 minutes
      }
    }
  });
};

/**
 * ChatBot - A complete, self-contained chatbot component
 * 
 * Features:
 * - JWT token parsing for automatic customer identification
 * - Popup and fullscreen modes with smooth transitions
 * - Minimize/restore functionality
 * - Session persistence and management
 * - Welcome message tracking across sessions
 * - Responsive design for mobile and desktop
 * 
 * @example
 * ```tsx
 * <ChatBot
 *   serverUrl="https://api.example.com"
 *   agentId="agent-123"
 *   authToken="eyJhbGc..."
 *   enableLogging={true}  // Enable console logging for debugging
 * />
 * ```
 */
export const ChatBot: React.FC<ChatBotProps> = ({
  serverUrl,
  agentId,
  agentName,
  authToken,
  authProvider = 'generic',
  customerId: propCustomerId,
  customerName: propCustomerName,
  language: propLanguage,
  initialMode = 'popup',
  pollingConfig,
  onSessionCreated,
  onClose,
  onDisplayModeChange,
  autoStartSession: _autoStartSession = true,
  requireAuth = false,
  attribution = 'enter1nine',
  enableLogging = false,
  layoutDebug = false,
  forceMinimize = false,
  welcomeMessages,
  layout,
  renderSettingsModal
}) => {
  // Note: autoStartSession is kept for API compatibility but internal session
  // management is handled automatically by useChatSessionState hook
  void _autoStartSession;
  // Configure logging based on prop
  useEffect(() => {
    setLoggingEnabled(enableLogging || layoutDebug);
  }, [enableLogging, layoutDebug]);

  // Validate JWT token in development mode
  useEffect(() => {
    if (authToken && enableLogging) {
      try {
        // Try to parse the token
        const payload = parseJWT(authToken);

        // Check if token is expired
        if (isJWTExpired(authToken)) {
          logWarn('JWT token is expired. Authentication requests will likely fail.');
        }

        // Check if required claims are present
        if (!payload.sub) {
          logError('JWT token is missing required "sub" claim. Customer identification will fail.');
        }

        // Log successful validation
        log('JWT token validated successfully', {
          hasSubClaim: !!payload.sub,
          hasNameClaim: !!payload.name,
          isExpired: isJWTExpired(authToken),
        });
      } catch (error) {
        logError('Failed to parse JWT token. Token may be malformed.', error);
      }
    }
  }, [authToken, enableLogging]);

  // Determine language: use prop if provided, otherwise detect from browser
  // Browser detection: 'en' or 'da' if browser is English/Danish, otherwise 'en'
  const language = useMemo<Language>(
    () => propLanguage ?? detectBrowserLanguage(),
    [propLanguage]
  );

  // Create QueryClient instance (memoized per component instance)
  const queryClient = useMemo(() => createQueryClient(), []);

  // Extract customer info using custom hook
  const customerInfo = useCustomerInfo({
    propCustomerId,
    propCustomerName,
    authToken,
    authProvider
  });

  // Track welcome message state using custom hook
  const { markWelcomeShown, hasShownWelcome: hasSessionShownWelcome } = useSessionWelcomeTracking();

  // Display mode management hook
  const {
    isChatEnabled,
    isChatMinimized,
    isFullScreen,
    minimizeChat: handleMinimizeChat,
    restoreChat: handleRestoreChat,
    expandToFullscreen: handleExpand,
    contractFromFullscreen: handleContract
  } = useChatDisplayMode({
    initialMode,
    forceMinimize,
    onDisplayModeChange,
    onClose
  });

  // Session state management hook
  const {
    currentSessionId,
    sessionKey,
    handleSessionCreated,
    handleSessionUpdate
  } = useChatSessionState({
    agentId,
    customerId: customerInfo.customerId,
    onSessionCreated
  });

  // Input message state - lifted up to persist across popup/fullscreen mode changes
  const [inputMessage, setInputMessage] = useState<string>('');

  // Shared chat state - lifted up to persist across popup/fullscreen mode changes
  // This includes messages, status, events, and polling offset
  const { state: sharedState, setters, resetState } = useSharedChatState();

  // Extend setters with resetSharedState for child components
  const sharedStateSetters = useMemo(() => ({
    ...setters,
    resetSharedState: resetState,
  }), [setters, resetState]);

  // Track previous session ID for detecting session changes
  const prevSessionIdRef = useRef<string | null>(null);

  // Reset shared state when switching to a different session
  useEffect(() => {
    // Don't reset on initial null -> sessionId transition
    if (prevSessionIdRef.current !== null && currentSessionId !== prevSessionIdRef.current) {
      resetState();
      setInputMessage(''); // Clear draft when switching sessions
    }
    prevSessionIdRef.current = currentSessionId;
  }, [currentSessionId, resetState]);

  /**
   * Handles when the welcome message is shown
   */
  const handleWelcomeShown = useCallback(() => {
    if (currentSessionId) {
      markWelcomeShown(currentSessionId);
      log('Welcome message shown for session:', currentSessionId);
    }
  }, [currentSessionId, markWelcomeShown]);


  // Layout configuration defaults
  const allowFullscreen = layout?.allowFullscreen !== false;
  const showRestoreButton = layout?.showRestoreButton !== false;
  
  // Scroll configuration defaults
  const scrollToNewMessage = layout?.scroll?.scrollToNewMessage !== false;
  const respectUserScroll = layout?.scroll?.respectUserScroll !== false;

  return (
    <QueryClientProvider client={queryClient}>
      {/* Popup Chat Window */}
      {isChatEnabled && !isChatMinimized && !isFullScreen && (
        <div
          className={`fixed inset-2 sm:bottom-4 sm:right-4 sm:inset-auto z-50 w-[calc(100vw-1rem)] sm:w-[400px] md:w-[500px] h-[calc(100dvh-1rem)] sm:h-[600px] md:h-[700px] max-w-[500px] animate-in slide-in-from-bottom-5 duration-300 ${layout?.containerClassName || ''}`}
          style={{
            paddingTop: 'max(0.5rem, env(safe-area-inset-top))',
            paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))'
          }}
        >
          <ChatWindow
            key={`chat-${sessionKey}-${currentSessionId || 'new'}`}
            serverUrl={serverUrl}
            agentId={agentId}
            customerId={customerInfo.customerId}
            customerName={customerInfo.customerName}
            agentName={agentName}
            authToken={authToken}
            requireAuth={requireAuth}
            scrollToNewMessage={scrollToNewMessage}
            respectUserScroll={respectUserScroll}
            language={language}
            sessionId={currentSessionId}
            hasShownWelcome={hasSessionShownWelcome(currentSessionId)}
            onSessionCreated={handleSessionCreated}
            onWelcomeShown={handleWelcomeShown}
            onClose={handleMinimizeChat}
            onExpand={allowFullscreen ? handleExpand : undefined}
            pollingConfig={pollingConfig}
            attribution={attribution}
            welcomeMessages={welcomeMessages}
            headerConfig={layout?.header}
            className={layout?.windowClassName}
            inputMessage={inputMessage}
            onInputMessageChange={setInputMessage}
            debugMode={enableLogging}
            layoutDebug={layoutDebug}
            sharedState={sharedState}
            sharedStateSetters={sharedStateSetters}
          />
        </div>
      )}

      {/* Full Screen Chatbot */}
      {isFullScreen && isChatEnabled && !isChatMinimized && (
        <FullScreenChatbot
          key={`fullscreen-${sessionKey}-${currentSessionId || 'new'}`}
          serverUrl={serverUrl}
          agentId={agentId}
          customerId={customerInfo.customerId}
          customerName={customerInfo.customerName}
          agentName={agentName}
          authToken={authToken}
          requireAuth={requireAuth}
          language={language}
          sessionId={currentSessionId}
          hasShownWelcome={hasSessionShownWelcome(currentSessionId)}
          onSessionCreated={handleSessionCreated}
          onWelcomeShown={handleWelcomeShown}
          onClose={handleMinimizeChat}
          onContract={handleContract}
          onSessionUpdate={handleSessionUpdate}
          pollingConfig={pollingConfig}
          attribution={attribution}
          welcomeMessages={welcomeMessages}
          headerConfig={layout?.header}
          sessionHistoryConfig={layout?.sessionHistory}
          mobileMenuConfig={layout?.mobileMenu}
          scrollConfig={layout?.scroll}
          renderSettingsModal={renderSettingsModal}
          inputMessage={inputMessage}
          onInputMessageChange={setInputMessage}
          debugMode={enableLogging}
          layoutDebug={layoutDebug}
          sharedState={sharedState}
          sharedStateSetters={sharedStateSetters}
        />
      )}

      {/* Floating Restore Button (when chat is minimized) */}
      {isChatEnabled && isChatMinimized && showRestoreButton && (
        <div className="fixed bottom-4 right-4 z-50">
          <button
            onClick={handleRestoreChat}
            className="bg-primary hover:bg-primary text-primary-foreground p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 animate-in slide-in-from-bottom-5"
            title="Restore Chat"
            aria-label="Restore Chat"
          >
            <MessageCircle size={24} />
          </button>
        </div>
      )}
    </QueryClientProvider>
  );
};

// Keep backward compatibility aliases
export const ParlantChatBot = ChatBot;
export type ParlantChatBotProps = ChatBotProps;

export default ChatBot;

